import { getGatewayTransport } from '~/apis/gatewayTransport'

import { createMargoCallAnalyticsClient } from '@mangovoice/margo-call-analytics-client'
import type { MargoCallReport } from '@mangovoice/margo-call-analytics-client'

export interface MargoCallReportRequestError {
  status: number
  error: string
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const parseReportField = (
  value: unknown,
  sourceIndex: number,
): MargoCallReport['fields'][number] | undefined => {
  if (
    !isRecord(value) ||
    typeof value.key !== 'string' ||
    typeof value.value !== 'string'
  ) {
    return undefined
  }

  return {
    key: value.key,
    value: value.value,
    order:
      typeof value.order === 'number' && Number.isFinite(value.order)
        ? value.order
        : sourceIndex,
  }
}

const parseMargoCallReport = (
  value: unknown,
  callId: number,
  pbxId: number,
): MargoCallReport | undefined => {
  if (!isRecord(value) || value.pbx_id !== pbxId || value.call_id !== callId) {
    return undefined
  }

  const fields = Array.isArray(value.fields)
    ? value.fields
        .map(parseReportField)
        .filter((field): field is MargoCallReport['fields'][number] => !!field)
    : []

  return {
    pbx_id: pbxId,
    call_id: callId,
    summary: typeof value.summary === 'string' ? value.summary : '',
    fields,
    ...(typeof value.transcript === 'string'
      ? { transcript: value.transcript }
      : {}),
  }
}

const getErrorCode = (value: unknown) =>
  isRecord(value) && typeof value.error === 'string'
    ? value.error
    : 'request_failed'

export const fetchMargoCallReport = async (
  callId: number,
  pbxId: number,
): Promise<MargoCallReport | null> => {
  const response = await createMargoCallAnalyticsClient(
    getGatewayTransport(),
  ).getCallReport(callId, pbxId)

  const errorCode = getErrorCode(response.data)

  if (response.status === 404 && errorCode === 'not_found') return null

  if (!response.ok) {
    throw {
      status: response.status,
      error: errorCode,
    } satisfies MargoCallReportRequestError
  }

  const report = parseMargoCallReport(response.data, callId, pbxId)

  if (!report) {
    throw {
      status: response.status,
      error: 'invalid_response',
    } satisfies MargoCallReportRequestError
  }

  return report
}
